import { useState } from 'react'
import type { SatelliteFacets, SatelliteFacetValue } from '../api'
import { t as tAmbient, useT } from '../i18n'
import { categoryLabel } from './satLayer'
import { discoverySourceLabel } from './SatelliteCard'
import { statusLabel } from './statusLabel'
import {
  FILTER_DIMS,
  countActive,
  type FilterDim,
  type FilterState,
} from './satFilter'

interface FilterPanelProps {
  facets: SatelliteFacets
  filter: FilterState
  onToggle: (dim: FilterDim, value: string) => void
  onReset: () => void
  /** Сколько позиций проходит фильтр (маркеров на карте). */
  visible: number
  /** Всего позиций на карте (числовые координаты). */
  total: number
}

// Ключи заголовков групп (порядок = FILTER_DIMS).
const GROUP_TITLE_KEY: Record<FilterDim, string> = {
  category: 'satellites.filter.category',
  company: 'satellites.filter.company',
  orbit_class: 'satellites.filter.orbitClass',
  legal_entity: 'satellites.filter.legalEntity',
  status: 'satellites.filter.status',
  source: 'satellites.filter.source',
}

// Человекочитаемые подписи технических значений орбиты.
const ORBIT_CLASS_KEY: Record<string, string> = {
  polar: 'satellites.orbit.polar',
  inclined: 'satellites.orbit.inclined',
  equatorial: 'satellites.orbit.equatorial',
}
/** Подпись значения фасета для измерения. Категорию берём из CATEGORY_META,
 *  орбиту/статус — из локальных словарей, компанию/юрлицо — как есть. */
function facetLabel(dim: FilterDim, value: string): string {
  switch (dim) {
    case 'category':
      return categoryLabel(value)
    case 'orbit_class':
      return ORBIT_CLASS_KEY[value] ? tAmbient(ORBIT_CLASS_KEY[value]) : value
    case 'status':
      return statusLabel(value)
    case 'source':
      return discoverySourceLabel(value) ?? value
    default:
      return value
  }
}

/** Один пункт-чекбокс фасета (значение + число спутников). */
function FacetRow({
  checked,
  label,
  count,
  onToggle,
}: {
  checked: boolean
  label: string
  count: number
  onToggle: () => void
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      onClick={onToggle}
      className="flex w-full items-center gap-2 rounded-lg px-1.5 py-1 text-left text-xs text-mist transition-colors hover:bg-haze"
    >
      <span
        aria-hidden
        className={[
          'flex h-3.5 w-3.5 flex-shrink-0 items-center justify-center rounded border transition-colors',
          checked ? 'border-sonar bg-sonar text-deep' : 'border-hull bg-transparent text-transparent',
        ].join(' ')}
      >
        <svg viewBox="0 0 12 12" className="h-2.5 w-2.5" aria-hidden>
          <path
            d="M2.5 6.5l2.5 2.5 4.5-5"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
      <span className="flex-1 truncate">{label}</span>
      <span className="shrink-0 tabular-nums text-mist/70">{count}</span>
    </button>
  )
}

/** Панель фасетных фильтров карты спутников. В покое — компактная закладка с
 *  бейджем числа активных фильтров; по клику раскрывается тело с 5 группами. */
export function FilterPanel({ facets, filter, onToggle, onReset, visible, total }: FilterPanelProps) {
  const t = useT()
  const [open, setOpen] = useState(false)
  const active = countActive(filter)

  return (
    <div className="pointer-events-auto w-64 overflow-hidden rounded-xl border border-hull bg-abyss/85 shadow-console backdrop-blur">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 px-3.5 py-2.5 text-left"
      >
        <svg
          viewBox="0 0 12 12"
          className={['h-3 w-3 shrink-0 opacity-70 transition-transform', open ? '' : '-rotate-90'].join(' ')}
          aria-hidden
        >
          <path
            d="M2 4l4 4 4-4"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <span className="text-sm font-semibold text-foam">{t('satellites.filter.title')}</span>
        {active > 0 && (
          <span className="rounded-full bg-sonar px-1.5 py-0.5 text-[10px] font-semibold leading-none text-deep">
            {active}
          </span>
        )}
        <span className="ml-auto shrink-0 text-[11px] tabular-nums text-mist">
          {visible} / {total}
        </span>
      </button>

      {open && (
        <div className="console-scroll max-h-[calc(100vh-8rem)] overflow-y-auto border-t border-hull-hair px-3 pb-3 pt-1">
          {FILTER_DIMS.map(dim => {
            const values: SatelliteFacetValue[] = facets[dim] ?? []
            if (values.length === 0) return null
            const selected = filter[dim]
            return (
              <div key={dim} className="mt-2 first:mt-1">
                <div className="px-1.5 pb-0.5 text-[10px] font-semibold uppercase tracking-wide text-mist/70">
                  {t(GROUP_TITLE_KEY[dim])}
                </div>
                {values.map(v => (
                  <FacetRow
                    key={v.value}
                    checked={selected.includes(v.value)}
                    label={facetLabel(dim, v.value)}
                    count={v.count}
                    onToggle={() => onToggle(dim, v.value)}
                  />
                ))}
              </div>
            )
          })}

          {active > 0 && (
            <button
              type="button"
              onClick={onReset}
              className="mt-3 w-full rounded-lg border border-hull px-2 py-1.5 text-xs text-mist transition-colors hover:bg-haze hover:text-foam"
            >
              {t('satellites.filter.reset', { count: active })}
            </button>
          )}
        </div>
      )}
    </div>
  )
}
